import { CircleAlert } from "lucide-react";
import { PrivacyPolicySection, Fill, MailLink } from "./PrivacyPolicyUI";
import {
  privacyPolicydataCollection,
  privacyPolicyretentionData,
  privacyPolicyserviceProviders,
} from "./privacyPolicyData";

export const PrivacyPolicyContent = () => {
  return (
    <article className="min-w-0 flex-1 rounded-xl border border-[#e4e8f5] bg-white px-[34px] pb-8 pt-1.5 max-[900px]:w-full max-[640px]:px-[18px] max-[640px]:pb-6">
      {/* Who we are */}
      <PrivacyPolicySection id="who-we-are" title="Who we are">
        <p className="mb-2.5 text-[14.5px]">
          <Fill>Jai Bharat Construction Company</Fill>,{" "}
          <Fill>162, Sector 7, Panchkula, Haryana, 134109</Fill>, India.
        </p>

        <p className="mb-2.5 text-[14.5px] last:mb-0">
          Privacy contact: <MailLink email="privacy@example.com" /> · Grievance
          Officer (DPDP Act 2023): <Fill>Officer Name</Fill>,{" "}
          <MailLink email="grievance@example.com" />
        </p>
      </PrivacyPolicySection>

      {/* Data we collect */}
      <PrivacyPolicySection
        id="data-we-collect"
        title="Data we collect and why"
      >
        <div className="my-2.5 overflow-hidden rounded-[9px] border border-[#e4e8f5]">
          <table className="w-full border-collapse text-[13.5px]">
            <thead>
              <tr className="bg-[#f4f6fc] text-left">
                <th className="border-b-2 border-[#e4e8f5] px-[13px] py-[9px] text-[10.5px] font-extrabold uppercase tracking-[0.7px] text-[#6b7aab]">
                  Data
                </th>
                <th className="border-b-2 border-[#e4e8f5] px-[13px] py-[9px] text-[10.5px] font-extrabold uppercase tracking-[0.7px] text-[#6b7aab]">
                  Purpose
                </th>
              </tr>
            </thead>

            <tbody>
              {privacyPolicydataCollection.map((item) => (
                <tr key={item.data} className="even:bg-[#f9fafd]">
                  <td className="border-b border-[#eef0f9] px-[13px] py-2.5 align-top font-semibold text-[#1a2b6b] last:border-b-0">
                    {item.data}
                  </td>
                  <td className="border-b border-[#eef0f9] px-[13px] py-2.5 align-top last:border-b-0">
                    {item.purpose}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Callout */}
        <div className="my-3.5 flex gap-[11px] rounded-[9px] border border-[#f5d57b] bg-[#fef3e2] px-3.5 py-3 text-[13.5px] leading-[1.5] text-[#7a4a00]">
          <CircleAlert />

          <div>
            <strong className="font-semibold text-[#1a2b6b]">
              Location is collected in the foreground only
            </strong>{" "}
            — at the moment you submit a visit, check, dispatch or complaint.
            JBCC does not track location in the background or when the app is
            closed.
          </div>
        </div>

        <p className="mb-2.5 text-[14.5px] last:mb-0">
          JBCC contains{" "}
          <strong className="font-semibold text-[#1a2b6b]">
            no advertising SDK
          </strong>
          . We do not sell your data, share it with data brokers, or use it for
          advertising or profiling.
        </p>
      </PrivacyPolicySection>

      {/* Legal basis */}
      <PrivacyPolicySection id="legal-basis" title="Legal basis">
        <p className="mb-2.5 text-[14.5px] last:mb-0">
          Data is processed under your employment or contracting relationship
          with the company for legitimate business operations. You consent at
          first login and through your device's permission prompts, which are
          preceded by an in-app disclosure screen. The company is the Data
          Fiduciary under India's DPDP Act 2023.
        </p>
      </PrivacyPolicySection>

      {/* Who we share it with */}
      <PrivacyPolicySection id="sharing" title="Who we share it with">
        <p className="mb-2.5 text-[14.5px]">
          Internally: your admins, supervisors and management for expense
          claims.
        </p>

        <p className="mb-2.5 text-[14.5px]">Service providers:</p>

        <ul className="my-2.5 flex flex-col gap-[7px]">
          {privacyPolicyserviceProviders.map((provider) => (
            <li
              key={provider.name}
              className="relative pl-[19px] text-[14.5px] before:absolute before:left-1 before:top-[9px] before:h-1.5 before:w-1.5 before:rounded-full before:bg-[#c8d0f0] before:content-['']"
            >
              <Fill>{provider.name}</Fill> — {provider.description}
            </li>
          ))}
        </ul>

        <p className="mb-2.5 text-[14.5px] last:mb-0">
          We may also disclose data where required by law.
        </p>
      </PrivacyPolicySection>

      {/* Retention */}
      <PrivacyPolicySection id="retention" title="How long we keep it">
        <div className="my-2.5 overflow-hidden rounded-[9px] border border-[#e4e8f5]">
          <table className="w-full border-collapse text-[13.5px]">
            <thead>
              <tr className="bg-[#f4f6fc] text-left">
                <th className="border-b-2 border-[#e4e8f5] px-[13px] py-[9px] text-[10.5px] font-extrabold uppercase tracking-[0.7px] text-[#6b7aab]">
                  Data
                </th>
                <th className="border-b-2 border-[#e4e8f5] px-[13px] py-[9px] text-[10.5px] font-extrabold uppercase tracking-[0.7px] text-[#6b7aab]">
                  Retention
                </th>
              </tr>
            </thead>

            <tbody>
              {privacyPolicyretentionData.map((item) => (
                <tr key={item.data} className="even:bg-[#f9fafd]">
                  <td className="border-b border-[#eef0f9] px-[13px] py-2.5 align-top font-semibold text-[#1a2b6b]">
                    {item.data}
                  </td>
                  <td className="border-b border-[#eef0f9] px-[13px] py-2.5 align-top">
                    {item.retention}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </PrivacyPolicySection>

      {/* Account and deletion */}
      <PrivacyPolicySection id="deletion" title="Account and data deletion">
        <p className="mb-2.5 text-[14.5px]">
          JBCC accounts are created and deactivated by a company administrator;
          there is no self-registration and no in-app self-service deletion.
        </p>

        <p className="mb-2.5 text-[14.5px] last:mb-0">
          To request deletion of your account and associated personal data,
          email <MailLink email="privacy@example.com" /> from your work address
          with your employee ID. We respond within 30 days. Records inside a
          statutory retention period (for example expense claims) cannot be
          deleted on request.
        </p>
      </PrivacyPolicySection>

      {/* Security */}
      <PrivacyPolicySection id="security" title="Security">
        <p className="mb-2.5 text-[14.5px] last:mb-0">
          Data is encrypted in transit (TLS 1.2+) and at rest. Access is
          role-based, passwords are stored as salted hashes, session tokens are
          held in the platform keystore, and inactive sessions expire
          automatically.
        </p>
      </PrivacyPolicySection>

      {/* Rights */}
      <PrivacyPolicySection id="rights" title="Your rights">
        <p className="mb-2.5 text-[14.5px] last:mb-0">
          You may request access to or correction of your data, request
          deletion, or raise a grievance. Write to{" "}
          <MailLink email="privacy@example.com" /> or the Grievance Officer at{" "}
          <MailLink email="grievance@example.com" />. We reply within 30 days.
        </p>
      </PrivacyPolicySection>

      {/* Children */}
      <PrivacyPolicySection id="children" title="Children">
        <p className="mb-2.5 text-[14.5px] last:mb-0">
          JBCC is not directed at anyone under 18 and is available only to
          company employees and contractors.
        </p>
      </PrivacyPolicySection>

      {/* Changes */}
      <PrivacyPolicySection id="changes" title="Changes">
        <p className="mb-2.5 text-[14.5px] last:mb-0">
          Material changes are notified in-app at login and by email at least 7
          days before they take effect.
        </p>
      </PrivacyPolicySection>
    </article>
  );
};
